import * as uuid from 'uuid';
import FlexApi, { DuplicateShiftBooking } from 'utils/FlexApi';
import React, { useEffect, useState } from 'react';
import Spinner from '@indeed/frontend-components-react/components/Spinner';
import { Alert } from '@indeed/ifl-components/components/Alert';
import {
    Box,
    Button,
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogPopup,
    Divider,
    RadioFormField,
} from '@indeed/ifl-components';
import { ClientConfigType } from 'client/context/ClientConfigContext';
import { DateTime } from 'luxon';
import { DuplicateShiftBookingError } from 'utils/FlexApi/errors';
import { Formik, Form as FormikForm, FormikHelpers } from 'formik';
import { FormikInputDateField, FormikInputField, FormikTextAreaField, SubmitButton } from './index';
import { MAX_ALLOWED_SHIFT_HOURS } from 'constants/index';
import { datadogRum } from '@datadog/browser-rum';
import { object, string } from 'yup';
import { useLocalizationContext } from 'client/context/LocalizationContext';

const currentDate = DateTime.now().toISODate();

export interface ShiftCreationType {
    shiftBookingId?: string;
    client: string;
    breakDuration: number;
    timezone: string;
    payRate: string;
    currency: string;
    role: string;
    venue: string;
}

interface FormValuesType {
    shiftOption: ShiftCreationType | undefined;
    shiftStartDate: string;
    shiftEndDate: string;
    startTime: string;
    endTime: string;
    supervisor: string;
    notes: string;
}

const initialValues: FormValuesType = {
    shiftOption: undefined,
    shiftStartDate: currentDate,
    shiftEndDate: currentDate,
    startTime: '08:00',
    endTime: '17:00',
    supervisor: '',
    notes: '',
};

export interface ShiftsListType {
    children: React.JSX.Element;
    value: string;
}

interface AdhocShiftSelectFormProps extends React.PropsWithChildren<{}> {
    clientConfig: ClientConfigType;
    pastShiftsList: ShiftsListType[];
    escapeUri: string;
}

const AdhocShiftSelectForm = (props: AdhocShiftSelectFormProps): React.JSX.Element => {
    const { gt } = useLocalizationContext();
    const [validationError, setValidationError] = useState<string>();
    const [formError, setFormError] = useState<string>();
    const [shiftSelected, setShiftSelected] = React.useState<ShiftCreationType>();
    const adhocShiftsFormErrorId = 'Error-adhocShiftsFormError';

    useEffect(() => {
        if (formError) {
            document.getElementById(adhocShiftsFormErrorId)?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [formError]);

    const onSubmit = (values: FormValuesType, meta: FormikHelpers<FormValuesType>): void => {
        const requestId = uuid.v4();
        if (!shiftSelected) {
            meta.setSubmitting(false);
            setValidationError(gt.gettext('Form was submitted but a shift was not selected.'));

            return;
        }
        const shiftStart = DateTime.fromISO(`${values.shiftStartDate}T${values.startTime}`, {
            zone: shiftSelected.timezone,
        });
        const shiftEnd = DateTime.fromISO(`${values.shiftEndDate}T${values.endTime}`, {
            zone: shiftSelected.timezone,
        });
        const shiftHours = shiftEnd.diff(shiftStart, 'hours').valueOf() / (1000 * 60 * 60);
        if (shiftHours > MAX_ALLOWED_SHIFT_HOURS) {
            meta.setSubmitting(false);
            setValidationError(
                gt.gettext('Shifts may not exceed %s hours. Please revise your start and end time.', [
                    MAX_ALLOWED_SHIFT_HOURS,
                ])
            );

            return;
        }
        const duplicateShiftBooking: DuplicateShiftBooking = {
            shiftBookingId: shiftSelected.shiftBookingId!,
            startTime: shiftStart.toJSDate(),
            endTime: shiftEnd.toJSDate(),
            supervisorName: values.supervisor,
            notes: values.notes,
        };
        if (!validationError) {
            const countryCode = props.clientConfig['country-code'] === 'US' ? 'US' : 'GB';
            const flexApi = new FlexApi(props.clientConfig.appUrl!, countryCode);
            flexApi
                .duplicateShiftBooking(duplicateShiftBooking, { requestId })
                .then((newShiftBooking) => {
                    if (newShiftBooking) {
                        meta.setSubmitting(false);
                        setFormError(undefined);
                        window.location.replace(props.escapeUri);
                    }
                })
                .catch((e) => {
                    // meta.setSubmitting(false);
                    if (e instanceof DuplicateShiftBookingError || e instanceof Error) {
                        setFormError(e.message);
                    } else {
                        setFormError(gt.gettext('An unknown error occurred, please try again.'));
                    }
                    datadogRum.addError(e, { tags: { requestId } });
                })
                .finally(() => meta.setSubmitting(false));
        }
    };

    const validationSchema = object().shape({
        shiftStartDate: string().required(gt.gettext('Shift date is required')),
        startTime: string().required(gt.gettext('Shift start time is required')),
        endTime: string().required(gt.gettext('Shift end time is required')),
        supervisor: string().required(gt.gettext('Supervisor name is required')),
    });

    const customOnChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const shiftOption: ShiftCreationType = JSON.parse(e.target.value);
        setValidationError(undefined);
        setFormError(undefined);
        setShiftSelected(shiftOption);
    };

    const makeOvernightShift = (values: FormValuesType): boolean => {
        if (shiftSelected) {
            const shiftStart = DateTime.fromISO(`${values.shiftStartDate}T${values.startTime}`, {
                zone: shiftSelected.timezone,
            });
            const shiftEnd = DateTime.fromISO(`${values.shiftStartDate}T${values.endTime}`, {
                zone: shiftSelected.timezone,
            });
            if (shiftStart > shiftEnd) {
                values.shiftEndDate = DateTime.fromISO(values.shiftStartDate).plus({ days: 1 }).toISODate();

                return true;
            }
        }
        values.shiftEndDate = values.shiftStartDate;

        return false;
    };

    return (
        <>
            <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
                {(formik) =>
                    formik.isSubmitting && !formError ? (
                        <Box>
                            <Spinner size="md" label={gt.gettext('Creating shift ...')} />
                            <Box
                                sx={{
                                    padding: '5px 0px 10px 15px',
                                    display: 'inline-block',
                                    position: 'absolute',
                                }}
                            >
                                {gt.gettext('Creating shift ...')}
                            </Box>
                        </Box>
                    ) : (
                        <FormikForm className="mt-2">
                            <RadioFormField
                                name="shiftOption"
                                label={gt.gettext('Shift')}
                                showRequiredIndicator
                                items={props.pastShiftsList}
                                onChange={customOnChange}
                            />
                            {shiftSelected && (
                                <>
                                    <Divider style={{ marginBottom: '16px' }} />
                                    <FormikInputDateField
                                        label={gt.gettext('Shift start date')}
                                        fieldName="shiftStartDate"
                                        name="shiftStartDate"
                                        min={currentDate}
                                        showRequiredIndicator
                                    />
                                    <FormikInputField
                                        id="startTime"
                                        label={gt.gettext('Start time (%s):', [
                                            DateTime.now().setZone(shiftSelected.timezone).toFormat('ZZZZ'),
                                        ])}
                                        fieldName="startTime"
                                        inputType="time"
                                        showRequiredIndicator
                                    />
                                    <FormikInputField
                                        id="endTime"
                                        label={gt.gettext('End time (%s):', [
                                            DateTime.now().setZone(shiftSelected.timezone).toFormat('ZZZZ'),
                                        ])}
                                        fieldName="endTime"
                                        inputType="time"
                                        showRequiredIndicator
                                    />
                                    {validationError && (
                                        <Alert status="critical" sx={{ marginBlockEnd: 25 }}>
                                            <Box sx={{ maxInlineSize: '500px' }}>{validationError}</Box>
                                        </Alert>
                                    )}
                                    {makeOvernightShift(formik.values) && (
                                        <Alert status="warning" sx={{ marginBlockEnd: 25 }}>
                                            <Box sx={{ maxInlineSize: '500px' }}>
                                                {gt.gettext(
                                                    'Based on your times, this will be marked an overnight shift that ends on %s.',
                                                    [
                                                        DateTime.fromISO(formik.values.shiftEndDate).toLocaleString(
                                                            DateTime.DATE_MED
                                                        ),
                                                    ]
                                                )}
                                            </Box>
                                        </Alert>
                                    )}
                                    <FormikInputField
                                        id="supervisor"
                                        fieldName="supervisor"
                                        label={gt.gettext('Supervisor')}
                                        inputType="text"
                                        showRequiredIndicator
                                        helperText={gt.gettext('Provide the name of who asked you to work this shift.')}
                                    />
                                    <FormikTextAreaField
                                        fieldName="notes"
                                        label={gt.gettext('Notes')}
                                        helperText={gt.gettext(
                                            'Tell us why you are adding this shift. This will be reviewed by Flex'
                                        )}
                                    />
                                    {formError && (
                                        <Alert
                                            id={adhocShiftsFormErrorId}
                                            status="critical"
                                            sx={{ marginBlockEnd: 25 }}
                                        >
                                            <Box sx={{ maxInlineSize: '500px' }}>{formError}</Box>
                                        </Alert>
                                    )}
                                    <Box sx={{ paddingBlockEnd: 32 }}>
                                        <DialogPopup mobile>
                                            {({ triggerProps }) => (
                                                <>
                                                    <Button {...triggerProps} variant="primary">
                                                        {gt.gettext('Add Shift')}
                                                    </Button>
                                                    <DialogContent>
                                                        <DialogHeader>{gt.gettext('ATTENTION')}</DialogHeader>
                                                        <DialogBody>
                                                            {gt.gettext(
                                                                'Please ensure accuracy when creating this shift. If you do not work the shift or if you create a shift without authorization, you may be subject to discipline up to and including termination.'
                                                            )}
                                                        </DialogBody>
                                                        <DialogFooter>
                                                            <SubmitButton
                                                                text={gt.gettext('Agree and Add Shift')}
                                                                isSubmitting={formik.isSubmitting}
                                                            />
                                                        </DialogFooter>
                                                    </DialogContent>
                                                </>
                                            )}
                                        </DialogPopup>
                                    </Box>
                                </>
                            )}
                        </FormikForm>
                    )
                }
            </Formik>
        </>
    );
};

export default AdhocShiftSelectForm;
